/**
 * The outbound privacy boundary for signals the SDKs build for us.
 *
 * `captureException` sanitises what this code creates, but most spans are made
 * by instrumentations (document load, fetch, XHR) and most log records by the
 * framework. Those carry `url.full` with a query string, request headers and,
 * on an exception event, the raw message and stack. A processor sits between
 * the SDK and the exporter and rewrites each record before it is serialised,
 * so nothing reaches the network unsanitised.
 *
 * Mutation is deliberate: a `ReadableSpan`'s attribute bag is a plain object by
 * the time `onEnd` runs, and rewriting it in place keeps every SDK-owned field
 * (timing, ids, resource) intact — rebuilding a span would need an unsafe cast.
 *
 * @module lib/observability/redact
 */

import type { Context } from '@opentelemetry/api'
import type { LogRecordProcessor, ReadWriteLogRecord } from '@opentelemetry/sdk-logs'
import type { ReadableSpan, Span, SpanProcessor } from '@opentelemetry/sdk-trace-web'

import { isSensitiveKey, sanitizeAttributes, sanitizeStack, sanitizeText } from './sanitize'

/** Attributes whose value is a URL: the query string is dropped, not redacted. */
const URL_ATTRIBUTE = /^(url\.(full|query|fragment)|http\.url|http\.target)$/

/** Attribute keys whose value is a stack trace. */
const STACK_ATTRIBUTE = /stacktrace|stack$/

function stripQuery(value: string): string {
  const cut = value.search(/[?#]/)
  return cut === -1 ? value : value.slice(0, cut)
}

/**
 * Rewrite one attribute bag in place: credential-named keys are replaced,
 * URLs lose their query/fragment, stack values keep their frames but lose
 * anything header-shaped, and every other string is redacted and clipped.
 */
function scrubAttributeBag(bag: Record<string, unknown>): void {
  let sanitized: Record<string, string | number | boolean>
  try {
    sanitized = sanitizeAttributes(bag)
  } catch {
    return
  }

  for (const key of Object.keys(bag)) {
    try {
      delete bag[key]
    } catch {
      // A frozen bag cannot be rewritten; leaving it is safer than throwing.
      return
    }
  }

  for (const [key, value] of Object.entries(sanitized)) {
    if (isSensitiveKey(key)) {
      bag[key] = value
    } else if (URL_ATTRIBUTE.test(key) && typeof value === 'string') {
      bag[key] = stripQuery(value)
    } else if (STACK_ATTRIBUTE.test(key) && typeof value === 'string') {
      bag[key] = sanitizeStack(value)
    } else {
      bag[key] = value
    }
  }
}

/** Scrub one finished span: name, attributes, events and status message. */
export function scrubSpan(span: ReadableSpan): void {
  try {
    scrubAttributeBag(span.attributes as Record<string, unknown>)

    for (const event of span.events) {
      try {
        scrubAttributeBag(event.attributes as Record<string, unknown>)
        event.name = sanitizeText(event.name, 128)
      } catch {
        // One hostile event must not cost the whole span.
      }
    }

    const statusMessage = span.status?.message
    if (typeof statusMessage === 'string' && statusMessage.length > 0) {
      // The status object outlives span construction; rewrite its message in
      // place instead of replacing a read-only field.
      Object.assign(span.status, { message: sanitizeText(statusMessage, 256) })
    }
  } catch {
    // Telemetry must never break the export path.
  }
}

/**
 * Span processor that scrubs each span, then hands it to the real processor
 * (the batch processor that owns the exporter).
 */
export class RedactingSpanProcessor implements SpanProcessor {
  constructor(private readonly delegate: SpanProcessor) {}

  onStart(span: Span, parentContext: Context): void {
    this.delegate.onStart(span, parentContext)
  }

  onEnd(span: ReadableSpan): void {
    scrubSpan(span)
    this.delegate.onEnd(span)
  }

  forceFlush(): Promise<void> {
    return this.delegate.forceFlush()
  }

  shutdown(): Promise<void> {
    return this.delegate.shutdown()
  }
}

/** Scrub one log record in place: body, exception attributes and the bag. */
export function scrubLogRecord(record: ReadWriteLogRecord): void {
  try {
    scrubAttributeBag(record.attributes as Record<string, unknown>)
    if (typeof record.body === 'string') {
      record.body = sanitizeText(record.body, 1_024)
    }
    if (typeof record.eventName === 'string' && record.eventName.length > 0) {
      record.eventName = sanitizeText(record.eventName, 128)
    }
  } catch {
    // Telemetry must never break the export path.
  }
}

/** Log processor that scrubs each record, then delegates to the batch processor. */
export class RedactingLogRecordProcessor implements LogRecordProcessor {
  constructor(private readonly delegate: LogRecordProcessor) {}

  onEmit(record: ReadWriteLogRecord, context?: Context): void {
    scrubLogRecord(record)
    this.delegate.onEmit(record, context)
  }

  forceFlush(): Promise<void> {
    return this.delegate.forceFlush()
  }

  shutdown(): Promise<void> {
    return this.delegate.shutdown()
  }
}
